import React, { createContext, useContext, useEffect, useState } from 'react'
import { authAPI, User, LoginCredentials, ChangePasswordData } from '@/lib/auth'
import { setUnauthorizedCallback } from '@/lib/api-client'
import { toast } from 'sonner'

interface AuthContextType {
  user: User | null
  isLoading: boolean
  hasCurrentUser: boolean
  hasCachedUser: boolean,
  isLoginModalOpen: boolean
  showLoginModal: () => void
  hideLoginModal: () => void
  setCachedUser: (user: User) => void
  setLocalAndCachedUser: (user: User) => void
  clearCachedUser: () => void
  login: (credentials: LoginCredentials) => Promise<void>
  logout: () => Promise<void>
  logoutAll: () => Promise<void>
  changePassword: (data: ChangePasswordData) => Promise<void>
  sendPasswordResetLink: (email: string) => Promise<void>
  resetPassword: (data: {
    token: string
    email: string
    password: string
    password_confirmation: string
  }) => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {

  // const dispatch = useDispatch()
  // const cachedUser = useSelector((state: any) => state.user)

  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false)
  
  const showLoginModal = () => setIsLoginModalOpen(true)
  const hideLoginModal = () => setIsLoginModalOpen(false)

  
  // Set up unauthorized callback for API client
  useEffect(() => {

    const handleUnauthorized = () => {
      setUser(null) // should trigger modl popup to re-login
    }
    
    setUnauthorizedCallback(handleUnauthorized)
  }, [])

  // Load user on mount if saved in localstorage -> user will still need to authorise is server returns 401!
  useEffect(() => {
    const loadUser = async () => {
      
      await authAPI.setCsrfCookie();
      
      const cachedUser = getCachedUser();
      
      setUser(cachedUser ?? null);

      if (cachedUser) { // isonline too
        //update users local cache
        try {
          const { user } = await authAPI.getUser()
          setUser(user);
          setCachedUser(user); // fresh data from server
          setIsLoading(false)
        } catch (error) {
          setUser(null);
          setIsLoading(false)
        }
      } else {
        setIsLoading(false)
      }
    }
    loadUser()
  }, [])


  const login = async (credentials: LoginCredentials) => {

    await authAPI.setCsrfCookie(); // refecth in case its expired due to being idol

    try {
      setIsLoading(true)
      const response = await authAPI.login(credentials)
      setUser(response.user)
      setCachedUser(response.user)
      toast.success('Login successful!')
    } catch (error: any) {
      console.log(error);
      const message = error.response?.data?.message || 'Login failed'
      toast.error(message)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    try {
      await authAPI.logout()
      setUser(null)
      setCachedUser(null)
      toast.success('Logged out successfully')
    } catch (error: any) {
      console.error('Logout error:', error)
      setUser(null)
      toast.error('Logout failed')
    }
  }

  const logoutAll = async () => {
    try {
      await authAPI.logoutAll()
      setUser(null)
      setCachedUser(null)
      toast.success('Logged out from all devices')
    } catch (error: any) {
      console.error('Logout all error:', error)
      setUser(null)
      toast.error('Logout failed')
    }
  }

  const changePassword = async (data: ChangePasswordData) => {
    try {
      await authAPI.changePassword(data)
      toast.success('Password changed successfully')
    } catch (error: any) {
      const message = error.response?.data?.message || 'Password change failed'
      toast.error(message)
      throw error
    }
  }

  const sendPasswordResetLink = async (email: string) => {
    try {
      await authAPI.sendPasswordResetLink(email)
      toast.success('Password reset link sent to your email')
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to send reset link'
      toast.error(message)
      throw error
    }
  }

  const resetPassword = async (data: {
    token: string
    email: string
    password: string
    password_confirmation: string
  }) => {
    try {
      await authAPI.resetPassword(data)
      toast.success('Password reset successfully')
    } catch (error: any) {
      const message = error.response?.data?.message || 'Password reset failed'
      toast.error(message)
      throw error
    }
  }

  const refreshUser = async () => {
    try {
      const { user } = await authAPI.getUser()
      setUser(user)
      setCachedUser(user)
    } catch (error) {
      console.error('Failed to refresh user:', error)
    }
  }

  const getCachedUser = ():User | null => {
    // dispatch(updateUser(user));
    // const user = useSelector((state: any) => state.user.id ? state.user : null)
    const userData = localStorage.getItem('current_user');
    return userData ? JSON.parse(userData) : null;
  }

  const checkForChachedUser = (): boolean => {
    return !!getCachedUser()
  }


  const setCachedUser = async (user: User | null): void => {
    // dispatch(updateUser(user));
    // const user = useSelector((state: any) => state.user.id ? state.user : null)
    if (user) {
      localStorage.setItem('current_user', JSON.stringify(user))
    } else {
      localStorage.removeItem('current_user')
    }
  }

  const setLocalAndCachedUser = (user: User): void => {
    setCachedUser(user);
    setUser(user);
  }

  const clearCachedUser = (): void => {
    // dispatch(updateUser(null));
    localStorage.removeItem('current_user')
  }

  const hasCurrentUser = !!user // used to show login modal
  const hasCachedUser = !!checkForChachedUser() // used to show login page

  const value: AuthContextType = {
    user,
    isLoading,
    hasCurrentUser,
    hasCachedUser,
    isLoginModalOpen,
    setLocalAndCachedUser,
    showLoginModal,
    hideLoginModal,
    setCachedUser,
    clearCachedUser,
    login,
    logout,
    logoutAll,
    changePassword,
    sendPasswordResetLink,
    resetPassword,
    refreshUser
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}